import { Component } from '@angular/core';
import { IonicPage, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import { WeatherReadingsService } from '../../../services/weather-readings/weather-readings.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import {
  WeatherReading,
  WeatherReadingCreationModel,
  WeatherSetup
} from '../../../models/weather-readings/weather-readings.models';
import { DateService } from '../../../services/date/date.service';
import { WeatherLogPage } from '../weather-log/weather-log';
import { VesselService } from '../../../services/vessel/vessel.service';
import { VesselOption } from '../../../models/setup/setup.models';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { DateLostInTimeResult } from '../../../models/time-zones/time-zone.models';
import { cloneDeep } from 'lodash';
import { WeatherPermissions } from '../../../models/permissions/permission.models';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { AppConfig } from '../../../app/app.config';

@IonicPage()
@Component({
  selector: 'page-weather-reading-create',
  templateUrl: 'weather-reading-create.html',
})
export class WeatherReadingCreatePage {
  config = AppConfig.settings;

  dailyLogDate: PtLuxonDate = new PtLuxonDate();

  weatherReadingData: WeatherReading[] = [];
  mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  timeToShow: string;
  projectId: number;
  weatherSetups: WeatherSetup[];
  loading: boolean;
  displayDuplicateTimeErrorMessage: boolean;
  vesselEditor: VesselOption;
  isWeb: boolean;
  timeValid: boolean;
  currentTimeZoneText: string;
  weatherReadingDate: PtLuxonDate;
  dateLostInTime: boolean;
  lostInTimeResult: DateLostInTimeResult;
  permissions: WeatherPermissions = new WeatherPermissions();

  constructor(public navCtrl: NavController,
              public navParams: NavParams,
              public platform: Platform,
              private weatherReadingService: WeatherReadingsService,
              private dailyLogService: DailyLogService,
              private dateService: DateService,
              private dateParserService: DateParserService,
              private toastCtrl: ToastController,
              private vesselService: VesselService,
              private lockedDayService: LockedDayService,
              private timeZoneService: TimeZoneService,
              private permissionsService: PermissionsService,
              private appDataSyncService: AppDataSyncService,
  ) {
  }

  async ionViewDidLoad() {
    this.loading = true;
    this.isWeb = this.platform.is('core');
    this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    this.permissions = await this.permissionsService.weatherPermissions();

    this.vesselEditor = await this.vesselService.getEditor();

    await this.appDataSyncService.updateWeatherAppDataForDate(this.dailyLogDate);

    this.weatherReadingService.getWeatherSetupsForDay(this.dailyLogDate).then(setupData => {
      this.weatherSetups = setupData;
      this.projectId = this.weatherSetups[0].projectId;

      this.weatherReadingService.getWeatherLogDataForDay(this.dailyLogDate).then(readingData => {
        this.weatherReadingData = readingData;
        this.loading = false;
      });
    });
  }

  regexChange(inputTime: string) {
    if (inputTime.charAt(0) === '2') {
      this.mask[1] = new RegExp('[0-3]');
    } else {
      this.mask[1] = new RegExp('\\d');
    }
  }

  async timeAltered() {
    this.displayDuplicateTimeErrorMessage = false;
    this.timeValid = this.dateService.timeIsValid(this.timeToShow);

    if (!this.timeValid || this.timeToShow === '') {
      return;
    }

    this.weatherReadingDate = await this.dateService.setPtLuxonDateFromUserEnteredString(this.timeToShow, this.dailyLogDate.dateTime);

    if (this.weatherReadingDate.offsetNotNull()) {
      const timeHours = this.weatherReadingDate.dateTime.offset / 60;
      this.currentTimeZoneText = timeHours > 0 ? '+ ' + timeHours : timeHours.toString();
    }

    this.lostInTimeResult = cloneDeep(this.weatherReadingDate.isLostInTime);

    if (this.lostInTimeResult && this.lostInTimeResult.result) {
      this.dateLostInTime = true;
    } else {
      this.dateLostInTime = false;
    }
  }

  async save() {
    this.displayDuplicateTimeErrorMessage = false;
    const timeValid = await this.validateTime();

    if (!timeValid) {
      this.displayDuplicateTimeErrorMessage = true;
      return;
    }

    const readingCreation = new WeatherReadingCreationModel();
    readingCreation.dailyLogDate = this.dailyLogDate.dateTime;
    readingCreation.date = this.weatherReadingDate.dateTime;
    readingCreation.timeShow = this.timeToShow;
    readingCreation.projectId = this.projectId;
    readingCreation.vesselId = this.vesselEditor.id;
    readingCreation.lockedDay = false;

    // will return a reading if in locked day mode, or return null if not (because the reading will be saved to the db instead)
    let reading = await this.weatherReadingService.createNewWeatherReading(readingCreation);

    if (reading) {
      reading.localId = await this.weatherReadingService.getNewWeatherReadingLocalId();
      await this.weatherReadingService.saveWeatherReadingMultipleRecords([reading]);
    }

    this.presentSaveToast();

    this.navCtrl.setRoot(WeatherLogPage);
  }

  async validateTime(): Promise<boolean> {
    if (this.weatherReadingData.some(e => e.timeShow === this.timeToShow && (e.timeOffset * 60) === this.weatherReadingDate.dateTime.offset)) {
      return false;
    }

    return true;
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message: 'Weather Reading Created',
      duration: 3000,
      position: 'bottom'
    });

    return toast.present();
  }

  async offsetSelected(ptDate: PtLuxonDate) {
    this.displayDuplicateTimeErrorMessage = false;
  }
}



// WEBPACK FOOTER //
// ./src/pages/weather-readings/weather-reading-create/weather-reading-create.ts